// Marketing.jsx — manual-entry marketing analytics (no APIs needed).
// Lets the admin/marketing user log weekly social stats and see growth trends.

import React, { useState, useEffect, useMemo } from "react";
import { api, formatErr } from "./lib/api";
import { useAuth } from "./lib/auth";
import { mkC } from "./lib/chartFactory";
import { todayISO as todayIso } from "./lib/dates";
import { InfoTip } from "./components/InfoTip";
import { formatKSA, formatKSADate } from "./lib/ksaTime";

const PLATFORMS = [
  { key: "instagram_followers", label: "إنستغرام", color: "#E1306C", emoji: "📸", linkKey: "instagram" },
  { key: "tiktok_followers",    label: "تيك توك",  color: "#000000", emoji: "🎵", linkKey: "tiktok" },
  { key: "snapchat_followers",  label: "سناب شات", color: "#FFFC00", colorText: "#000", emoji: "👻", linkKey: "snapchat" },
  { key: "twitter_followers",   label: "إكس / تويتر", color: "#1DA1F2", emoji: "𝕏", linkKey: "twitter" },
];

const fmAr = (n) => Math.round(Number(n) || 0).toLocaleString("en-US");

// v119.201 — 🏆 لوحة شرف أفضل فكرة موظف (شهرية + إرسال واتساب للفريق).
export function HonorBoardCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);
  const canAnnounce = ["admin", "ceo", "marketing"].includes(user?.role);

  const load = () => {
    api.get("/idea-honor/current").then(({ data: d }) => setData(d)).catch(() => {});
  };
  useEffect(load, []);

  if (!data || !data.top?.length) return null;
  const best = data.top[0];

  const announce = async () => {
    setSending(true);
    try {
      const r = await api.post("/idea-honor/announce", { month: data.month });
      alert(`🏆 تم إرسال لوحة الشرف لواتساب الفريق (${r.data?.sent_to || 0} مستلم)`);
      load();
    } catch (e) { alert(formatErr(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="card" data-testid="idea-honor-board" style={{
      marginBottom: 14, padding: 16,
      background: "linear-gradient(135deg, #3E2723 0%, #5D4037 70%, #C9A227 140%)",
      color: "#FBF7F0",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 22 }}>🏆</span>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 11, opacity: 0.8, fontWeight: 700, letterSpacing: 1 }}>
            لوحة الشرف · أفضل فكرة موظف — {data.month}
          </div>
          <div style={{ fontSize: 15.5, fontWeight: 900, marginTop: 2 }} data-testid="honor-best-title">
            {best.title}
          </div>
          <div style={{ fontSize: 12, opacity: 0.9, marginTop: 3 }}>
            👤 {best.created_by} · ⭐ {best.success_chance}/100
            {data.announced && <span style={{ marginInlineStart: 8, opacity: 0.75 }}>✓ أُعلنت للفريق</span>}
          </div>
        </div>
        {canAnnounce && (
          <button type="button" onClick={announce} disabled={sending}
                  data-testid="honor-announce-btn"
                  style={{
                    background: "#C9A227", color: "#3E2723", border: "none",
                    padding: "8px 16px", borderRadius: 10, fontWeight: 800,
                    fontSize: 12.5, cursor: "pointer", fontFamily: "inherit",
                  }}>
            {sending ? "⏳ يُرسل…" : data.announced ? "📣 إعادة الإرسال" : "📣 إرسال للواتساب"}
          </button>
        )}
      </div>
      {data.top.length > 1 && (
        <div style={{ display: "flex", gap: 12, marginTop: 10, fontSize: 11.5, opacity: 0.85, flexWrap: "wrap" }}>
          {data.top.slice(1).map((t, i) => (
            <span key={t.id}>{i === 0 ? "🥈" : "🥉"} {t.title} — {t.created_by} ({t.success_chance}/100)</span>
          ))}
        </div>
      )}
    </div>
  );
}
const fmtDateAr = (d) => {
  try { return formatKSADate(new Date(d), { year: "numeric", month: "short", day: "numeric" }); }
  catch (e) { return d; }
};

function StatBadge({ delta, isPercent }) {
  if (delta === null || delta === undefined || Number.isNaN(delta)) {
    return <span style={{ fontSize: 11, color: "var(--muted)" }}>—</span>;
  }
  const v = Number(delta);
  if (v === 0) return <span className="b" style={{ background: "var(--beige)" }}>← ثابت</span>;
  const cls = v > 0 ? "bg" : "br_";
  const sign = v > 0 ? "▲" : "▼";
  const display = isPercent ? `${sign} ${Math.abs(v).toFixed(1)}%` : `${sign} ${fmAr(Math.abs(v))}`;
  return <span className={`b ${cls}`}>{display}</span>;
}

export default function Marketing() {
  const { user } = useAuth();
  const canEdit = user.role === "admin" || user.role === "marketing";

  const [snapshots, setSnapshots] = useState([]);
  const [links, setLinks] = useState({ linktree: "", instagram: "", tiktok: "", snapchat: "", twitter: "" });
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  // AI insights state
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiAt, setAiAt] = useState(null);

  // AI Q&A state
  const [qaInput, setQaInput] = useState("");
  const [qaHistory, setQaHistory] = useState([]); // {q, a, at}
  const [qaBusy, setQaBusy] = useState(false);

  const [linksDraft, setLinksDraft] = useState(links);
  const [savingLinks, setSavingLinks] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/marketing/snapshots"),
        api.get("/marketing/links"),
      ]);
      setSnapshots(s.data?.items || []);
      setLinks(l.data || {});
      setLinksDraft(l.data || {});
    } catch (e) {
      setMsg({ t: "r", m: formatErr(e.response?.data?.detail) || e.message });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const saveLinks = async () => {
    setSavingLinks(true); setMsg(null);
    try {
      await api.post("/marketing/links", linksDraft);
      setLinks(linksDraft);
      setMsg({ t: "g", m: "✅ تم حفظ الروابط" });
    } catch (e) {
      setMsg({ t: "r", m: formatErr(e.response?.data?.detail) || e.message });
    } finally { setSavingLinks(false); }
  };

  const deleteSnap = async (id) => {
    if (!window.confirm("حذف هذه القراءة؟")) return;
    try {
      await api.delete(`/marketing/snapshots/${id}`);
      await load();
    } catch (e) { setMsg({ t: "r", m: formatErr(e.response?.data?.detail) || e.message }); }
  };

  const runAi = async () => {
    setAiBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/ai/marketing-insights");
      setAiText(data.text || "");
      setAiAt(data.generated_at || new Date().toISOString());
    } catch (e) {
      setMsg({ t: "r", m: formatErr(e.response?.data?.detail) || e.message });
    } finally { setAiBusy(false); }
  };

  const askAi = async (overrideQuestion) => {
    const q = (overrideQuestion ?? qaInput).trim();
    if (!q || qaBusy) return;
    setQaBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/ai/marketing-ask", { question: q });
      setQaHistory((h) => [...h, { q, a: data.text || "", at: data.generated_at || new Date().toISOString() }]);
      setQaInput("");
    } catch (e) {
      setMsg({ t: "r", m: formatErr(e.response?.data?.detail) || e.message });
    } finally { setQaBusy(false); }
  };

  const QUICK_QUESTIONS = [
    "أفضل منصة هذا الأسبوع ولماذا؟",
    "ليش نقرات Linktree نزلت؟",
    "اقترح فكرة محتوى لتيك توك",
    "كيف أزيد متابعي سناب شات؟",
    "أعطني خطة محتوى لأسبوع كامل",
  ];

  // ── Excel report import — MOVED to Settings → UploadPanel.
  // Marketing tab is now a read-only viewer for snapshots. Uploading happens from
  // /إعدادات for consistency with all other Excel uploads (inv/cust/prod/stk/wst/cost/purch).

  // ── Analysis derived from snapshots ──
  const analysis = useMemo(() => {
    if (snapshots.length === 0) return null;
    const latest = snapshots[snapshots.length - 1];
    const previous = snapshots.length > 1 ? snapshots[snapshots.length - 2] : null;
    const oldest = snapshots[0];

    const perPlatform = PLATFORMS.map((p) => {
      const cur = Number(latest[p.key]) || 0;
      const prev = previous ? (Number(previous[p.key]) || 0) : null;
      const start = Number(oldest[p.key]) || 0;
      const wkDelta = prev !== null ? cur - prev : null;
      const wkPct = (prev !== null && prev > 0) ? ((cur - prev) / prev * 100) : null;
      const totalDelta = cur - start;
      const totalPct = start > 0 ? ((cur - start) / start * 100) : null;
      return { ...p, cur, prev, start, wkDelta, wkPct, totalDelta, totalPct };
    });

    // Best-growth platform (last entry vs previous)
    const withDelta = perPlatform.filter((p) => p.wkDelta !== null && p.cur > 0);
    const bestGrowth = withDelta.length ? [...withDelta].sort((a, b) => (b.wkPct || 0) - (a.wkPct || 0))[0] : null;
    const worstGrowth = withDelta.length ? [...withDelta].sort((a, b) => (a.wkPct || 0) - (b.wkPct || 0))[0] : null;

    // Linktree
    const visits = Number(latest.linktree_visits) || 0;
    const prevVisits = previous ? (Number(previous.linktree_visits) || 0) : null;
    const visitsDelta = prevVisits !== null ? visits - prevVisits : null;
    const clicks = Number(latest.linktree_clicks) || 0;
    const ctr = visits > 0 ? (clicks / visits * 100) : 0;

    // Engagement
    const likes = Number(latest.engagement_likes) || 0;
    const comments = Number(latest.engagement_comments) || 0;

    // Days since last entry
    const daysSinceLast = (() => {
      const d = new Date(latest.date); const now = new Date();
      return Math.floor((now - d) / (1000 * 60 * 60 * 24));
    })();

    return { latest, previous, oldest, perPlatform, bestGrowth, worstGrowth, visits, prevVisits, visitsDelta, clicks, ctr, likes, comments, daysSinceLast };
  }, [snapshots]);

  // ── Growth chart ──
  useEffect(() => {
    if (snapshots.length < 2) return;
    const labels = snapshots.map((s) => s.date.slice(5));
    const datasets = PLATFORMS
      .filter((p) => snapshots.some((s) => Number(s[p.key]) > 0))
      .map((p) => ({
        label: p.label,
        data: snapshots.map((s) => Number(s[p.key]) || 0),
        borderColor: p.color,
        backgroundColor: p.color + "22",
        tension: 0.3,
        pointRadius: 3,
        fill: false,
      }));
    if (datasets.length === 0) return;
    mkC("c-mkt-followers", "line", labels, datasets, {
      scales: { y: { beginAtZero: false, ticks: { callback: (v) => fmAr(v) } } },
    });

    if (snapshots.some((s) => Number(s.linktree_visits) > 0)) {
      mkC("c-mkt-linktree", "line", labels, [
        {
          label: "زيارات Linktree",
          data: snapshots.map((s) => Number(s.linktree_visits) || 0),
          borderColor: "#43E660",
          backgroundColor: "rgba(67,230,96,.15)",
          tension: 0.3,
          fill: true,
          pointRadius: 3,
          yAxisID: "y",
        },
        {
          label: "النقرات",
          data: snapshots.map((s) => Number(s.linktree_clicks) || 0),
          borderColor: "#1E6390",
          backgroundColor: "transparent",
          borderDash: [4, 3],
          tension: 0.3,
          fill: false,
          pointRadius: 2,
          yAxisID: "y1",
        },
      ], {
        scales: {
          y: { position: "right", beginAtZero: true, ticks: { callback: (v) => fmAr(v) } },
          y1: { position: "left", beginAtZero: true, grid: { display: false } },
        },
      });
    }
  });

  // ── Render ──
  if (loading) return <div className="card">⏳ جارٍ التحميل...</div>;

  return (
    <div data-testid="marketing-tab">
      {msg && <div className={`al al${msg.t}`}>{msg.m}</div>}

      <HonorBoardCard />

      {/* Reminder if no entries yet or data is stale */}
      {analysis && analysis.daysSinceLast >= 7 && (
        <div className="al alo" data-testid="stale-reminder">
          ⏰ آخر قراءة قبل {analysis.daysSinceLast} يوم — حدّث الأرقام للمتابعة الدقيقة لاتجاه النمو.
        </div>
      )}
      {snapshots.length === 0 && (
        <div className="card" style={{ marginBottom: 14, padding: 14, background: "var(--beige)" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--brown-deep)", marginBottom: 6 }}>👋 لا توجد قراءات بعد</div>
          <p style={{ fontSize: 12.5, color: "var(--muted)", lineHeight: 1.8, margin: 0 }}>
            ارفع ملف Excel لقراءات التسويق من <b>الإعدادات → رفع البيانات</b>. أنصح بإضافة قراءة أسبوعياً
            (مثلاً كل أحد) — كل ما زادت القراءات، صار التحليل أدق.
          </p>
        </div>
      )}

      {/* === Top KPIs === */}
      {analysis && (
        <>
          <h3 style={{ marginTop: 0, color: "var(--brown-deep)" }}>📊 ملخص آخر قراءة — {fmtDateAr(analysis.latest.date)}</h3>
          <div className="kg" data-testid="marketing-kpis">
            {analysis.perPlatform.filter((p) => p.cur > 0).map((p) => (
              <div key={p.key} className="kc" data-testid={`mkt-${p.linkKey}-card`}>
                <div className="kl" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 16 }}>{p.emoji}</span> {p.label}
                </div>
                <div className="kv" style={{ color: "var(--brown-deep)" }}>{fmAr(p.cur)}</div>
                <div className="ks" style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <StatBadge delta={p.wkDelta} />
                  {p.wkPct !== null && <span style={{ fontSize: 11, color: "var(--muted)" }}>{p.wkPct >= 0 ? "+" : ""}{p.wkPct.toFixed(1)}%</span>}
                  {p.totalDelta !== 0 && (
                    <span style={{ fontSize: 11, color: "var(--muted)", marginInlineStart: "auto" }}>
                      منذ البداية: {p.totalDelta > 0 ? "+" : ""}{fmAr(p.totalDelta)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Linktree + engagement row */}
          <div className="kg" style={{ marginTop: 12 }}>
            <div className="kc">
              <div className="kl">🔗 زيارات Linktree</div>
              <div className="kv" style={{ color: "var(--info)" }}>{fmAr(analysis.visits)}</div>
              <div className="ks" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <StatBadge delta={analysis.visitsDelta} />
                {analysis.prevVisits !== null && <span style={{ fontSize: 11, color: "var(--muted)" }}>السابق: {fmAr(analysis.prevVisits)}</span>}
              </div>
            </div>
            <div className="kc">
              <div className="kl">👆 النقرات</div>
              <div className="kv" style={{ color: "var(--success)" }}>{fmAr(analysis.clicks)}</div>
              <div className="ks">معدل النقر CTR: <b>{analysis.ctr.toFixed(1)}%</b></div>
            </div>
            <div className="kc">
              <div className="kl">❤ إعجابات</div>
              <div className="kv">{fmAr(analysis.likes)}</div>
              <div className="ks">آخر فترة</div>
            </div>
            <div className="kc">
              <div className="kl">💬 تعليقات</div>
              <div className="kv">{fmAr(analysis.comments)}</div>
              <div className="ks">آخر فترة</div>
            </div>
          </div>

          {/* Growth highlights */}
          {(analysis.bestGrowth || analysis.worstGrowth) && (
            <div className="g2" style={{ marginTop: 14 }}>
              {analysis.bestGrowth && analysis.bestGrowth.wkPct > 0 && (
                <div className="al alg" data-testid="best-growth">
                  🚀 <b>الأفضل نمواً</b>: {analysis.bestGrowth.label} — زاد {fmAr(analysis.bestGrowth.wkDelta)} متابع
                  ({analysis.bestGrowth.wkPct.toFixed(1)}%) منذ آخر قراءة. استمر في نوع المحتوى الحالي 👏
                </div>
              )}
              {analysis.worstGrowth && analysis.worstGrowth.wkPct < 0 && (
                <div className="al alr" data-testid="worst-growth">
                  ⚠ <b>تراجع</b>: {analysis.worstGrowth.label} — نقص {fmAr(Math.abs(analysis.worstGrowth.wkDelta))} متابع
                  ({Math.abs(analysis.worstGrowth.wkPct).toFixed(1)}%). راجع آخر منشور وحاول تنوع المحتوى.
                </div>
              )}
            </div>
          )}

          {/* === AI insights === */}
          <div className="card" data-testid="ai-marketing-card" style={{ marginTop: 14, background: "linear-gradient(135deg, var(--beige) 0%, var(--card) 100%)" }}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <div>
                <h4 style={{ margin: 0, color: "var(--brown-deep)" }}>🤖 تحليل تسويق ذكي</h4>
                <p style={{ margin: "4px 0 0", fontSize: 11.5, color: "var(--muted)" }}>
                  يحلّل Claude البيانات ويعطيك خطة عمل أسبوعية محددة.
                </p>
              </div>
              <button
                type="button"
                className="btn btn-success"
                disabled={aiBusy || snapshots.length < 1}
                onClick={runAi}
                data-testid="ai-marketing-run"
              >
                {aiBusy ? "⏳ جارٍ التحليل..." : aiText ? "🔄 تحليل جديد" : "🚀 ابدأ التحليل"}
              </button>
            </div>
            {aiText && (
              <div
                data-testid="ai-marketing-output"
                style={{ marginTop: 12, padding: 14, background: "var(--card)", borderRadius: 10, border: "1px solid var(--border)", whiteSpace: "pre-wrap", lineHeight: 1.85, fontSize: 13.5, color: "var(--brown-deep)" }}
              >
                {aiText}
                {aiAt && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--border)", fontSize: 11, color: "var(--muted)" }}>
                    🕒 {formatKSA(new Date(aiAt))}
                  </div>
                )}
              </div>
            )}
            {!aiText && snapshots.length === 1 && (
              <div style={{ marginTop: 10, fontSize: 12, color: "var(--muted)", lineHeight: 1.7 }}>
                💡 ملاحظة: لديك قراءة واحدة فقط الآن — التحليل سيكون أعمق بكثير لو أضفت قراءتين أو أكثر (يقدر يقارن النمو الأسبوعي).
              </div>
            )}
          </div>

          {/* === Ask Claude (Q&A) === */}
          <div className="card" data-testid="ai-marketing-qa-card" style={{ marginTop: 14 }}>
            <div style={{ marginBottom: 10 }}>
              <h4 style={{ margin: 0, color: "var(--brown-deep)" }}>💬 اسأل المحلل</h4>
              <p style={{ margin: "4px 0 0", fontSize: 11.5, color: "var(--muted)" }}>
                اطرح أي سؤال حول أداء التسويق (نمو منصة معينة، CTR، اقتراح حملة...) وسيُجيب Claude بناءً على القراءات.
              </p>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <textarea
                data-testid="ai-marketing-qa-input"
                rows="2"
                value={qaInput}
                onChange={(e) => setQaInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); askAi(); } }}
                placeholder="مثلاً: ليش متابعي سناب نزلوا الأسبوع الحالي؟ أو: اقترح فكرة محتوى للتيك توك."
                disabled={qaBusy}
                style={{ flex: "1 1 280px", minHeight: 48, padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 13, fontFamily: "inherit", resize: "vertical" }}
              />
              <button
                type="button"
                className="btn btn-success"
                onClick={() => askAi()}
                disabled={qaBusy || !qaInput.trim()}
                data-testid="ai-marketing-qa-send"
                style={{ alignSelf: "flex-start" }}
              >
                {qaBusy ? "⏳ يفكّر..." : "📩 إرسال"}
              </button>
            </div>

            {/* Quick-question chips */}
            <div data-testid="ai-marketing-qa-chips" style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
              <span style={{ fontSize: 11, color: "var(--muted)", alignSelf: "center", marginInlineEnd: 4 }}>أسئلة سريعة:</span>
              {QUICK_QUESTIONS.map((q, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => askAi(q)}
                  disabled={qaBusy}
                  data-testid={`ai-marketing-qa-chip-${i}`}
                  style={{
                    fontSize: 11.5,
                    padding: "4px 10px",
                    borderRadius: 999,
                    border: "1px solid var(--border)",
                    background: "var(--card)",
                    color: "var(--brown)",
                    cursor: qaBusy ? "not-allowed" : "pointer",
                    opacity: qaBusy ? 0.5 : 1,
                  }}
                >
                  {q}
                </button>
              ))}
            </div>
            {qaHistory.length > 0 && (
              <div data-testid="ai-marketing-qa-history" style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
                {[...qaHistory].reverse().map((it, i) => (
                  <div key={i} style={{ borderInlineStart: "3px solid var(--gold)", paddingInlineStart: 10 }}>
                    <div style={{ fontSize: 12, color: "var(--brown)", fontWeight: 700, marginBottom: 4 }}>
                      🙋 {it.q}
                    </div>
                    <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.85, fontSize: 13.5, color: "var(--brown-deep)" }}>
                      {it.a}
                    </div>
                    {it.at && (
                      <div style={{ marginTop: 6, fontSize: 10.5, color: "var(--muted)" }}>
                        🕒 {formatKSA(new Date(it.at))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Charts */}
          {snapshots.length >= 2 && (
            <>
              <div className="card" style={{ marginTop: 14 }}>
                <h4>📈 تطور المتابعين عبر الزمن</h4>
                <div className="cw" style={{ height: 300 }}><canvas id="c-mkt-followers"></canvas></div>
              </div>
              {snapshots.some((s) => Number(s.linktree_visits) > 0) && (
                <div className="card" style={{ marginTop: 14 }}>
                  <h4>🔗 زيارات ونقرات Linktree</h4>
                  <div className="cw" style={{ height: 260 }}><canvas id="c-mkt-linktree"></canvas></div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* === Excel report upload moved to Settings → UploadPanel for consistency.
            The marketing tab is now read-only for data; uploads happen from Settings. === */}

      {/* === Social links settings === */}
      {canEdit && (
        <div className="card" style={{ marginTop: 14 }} data-testid="links-form">
          <h3 style={{ marginTop: 0, color: "var(--brown-deep)" }}>🔗 روابط الحسابات</h3>
          <p style={{ fontSize: 12, color: "var(--muted)", marginTop: -4, marginBottom: 14 }}>
            احفظها مرة واحدة — تظهر كأزرار سريعة في الأعلى لتفتحها بضغطة.
          </p>
          <div className="g2" style={{ gap: 10 }}>
            <Field label="🔗 Linktree" value={linksDraft.linktree} onChange={(v) => setLinksDraft({ ...linksDraft, linktree: v })} type="url" testId="lk-linktree" />
            <Field label="📸 Instagram" value={linksDraft.instagram} onChange={(v) => setLinksDraft({ ...linksDraft, instagram: v })} type="url" testId="lk-instagram" />
            <Field label="🎵 TikTok" value={linksDraft.tiktok} onChange={(v) => setLinksDraft({ ...linksDraft, tiktok: v })} type="url" testId="lk-tiktok" />
            <Field label="👻 Snapchat" value={linksDraft.snapchat} onChange={(v) => setLinksDraft({ ...linksDraft, snapchat: v })} type="url" testId="lk-snapchat" />
            <Field label="𝕏 X / Twitter" value={linksDraft.twitter} onChange={(v) => setLinksDraft({ ...linksDraft, twitter: v })} type="url" testId="lk-twitter" />
          </div>
          <div style={{ marginTop: 14, display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" className="btn btn-soft" onClick={saveLinks} disabled={savingLinks} data-testid="save-links">
              {savingLinks ? "⏳ جارٍ الحفظ..." : "💾 حفظ الروابط"}
            </button>
          </div>
        </div>
      )}

      {/* Quick-open shortcuts (visible to everyone if links saved) */}
      {Object.values(links).some((v) => v) && (
        <div className="card" style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 8 }} data-testid="quick-links">
          <b style={{ fontSize: 12.5, color: "var(--brown)", marginInlineEnd: "auto" }}>🚀 افتح الحسابات</b>
          {links.linktree && <a href={links.linktree} target="_blank" rel="noopener noreferrer" className="btn btn-soft" style={{ fontSize: 12 }}>🔗 Linktree</a>}
          {links.instagram && <a href={links.instagram} target="_blank" rel="noopener noreferrer" className="btn btn-soft" style={{ fontSize: 12 }}>📸 Instagram</a>}
          {links.tiktok && <a href={links.tiktok} target="_blank" rel="noopener noreferrer" className="btn btn-soft" style={{ fontSize: 12 }}>🎵 TikTok</a>}
          {links.snapchat && <a href={links.snapchat} target="_blank" rel="noopener noreferrer" className="btn btn-soft" style={{ fontSize: 12 }}>👻 Snapchat</a>}
          {links.twitter && <a href={links.twitter} target="_blank" rel="noopener noreferrer" className="btn btn-soft" style={{ fontSize: 12 }}>𝕏 X</a>}
        </div>
      )}

      {/* History table */}
      {snapshots.length > 0 && (
        <div className="card" style={{ marginTop: 14 }} data-testid="history-table">
          <h3 style={{ marginTop: 0, color: "var(--brown-deep)" }}>📋 سجل القراءات ({snapshots.length})</h3>
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>التاريخ</th>
                  <th>📸 IG</th>
                  <th>🎵 TT</th>
                  <th>👻 SC</th>
                  <th>𝕏 X</th>
                  <th>🔗 زيارات</th>
                  <th>👆 نقرات</th>
                  <th>❤</th>
                  <th>💬</th>
                  <th>ملاحظة</th>
                  {canEdit && <th></th>}
                </tr>
              </thead>
              <tbody>
                {[...snapshots].reverse().map((s) => (
                  <tr key={s.id || s.date}>
                    <td>{fmtDateAr(s.date)}</td>
                    <td>{fmAr(s.instagram_followers)}</td>
                    <td>{fmAr(s.tiktok_followers)}</td>
                    <td>{fmAr(s.snapchat_followers)}</td>
                    <td>{fmAr(s.twitter_followers)}</td>
                    <td>{fmAr(s.linktree_visits)}</td>
                    <td>{fmAr(s.linktree_clicks)}</td>
                    <td>{fmAr(s.engagement_likes)}</td>
                    <td>{fmAr(s.engagement_comments)}</td>
                    <td style={{ fontSize: 11, color: "var(--muted)", maxWidth: 200 }}>{s.notes || "—"}</td>
                    {canEdit && (
                      <td>
                        <button
                          type="button"
                          onClick={() => deleteSnap(s.id)}
                          style={{ background: "transparent", border: "none", color: "var(--danger)", cursor: "pointer", fontSize: 14 }}
 
                          data-testid={`delete-${s.date}`}
                         aria-label="حذف">🗑</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "number", testId, suffix }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 12.5, color: "var(--brown)", fontWeight: 600 }}>{label}</label>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input
          type={type}
          {...(type === "number" ? { min: "0", step: "1" } : {})}
          data-testid={testId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ flex: 1, padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 13.5, fontFamily: "var(--f-n)" }}
        />
        {suffix && <span style={{ fontSize: 12, color: "var(--muted)" }}>{suffix}</span>}
      </div>
    </div>
  );
}
